import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { SITE } from "@/lib/site";

// Compute at request time — module-scope `new Date()` returns the epoch (1970)
// on Cloudflare Workers because Date is frozen until the first request.
function nextReportYear() {
  return new Date().getUTCFullYear() + 1;
}
function currentYear() {
  return new Date().getUTCFullYear();
}

export const Route = createFileRoute("/state-of-local-search/")({
  loader: () => ({
    nextReportYear: nextReportYear(),
    currentYear: currentYear(),
  }),
  head: () => {
    const NEXT_REPORT_YEAR = nextReportYear();
    const url = `${SITE.url}/state-of-local-search`;
    return {
      meta: [
        { title: `State of Local Search ${NEXT_REPORT_YEAR} — Annual Report | ${SITE.name}` },
        {
          name: "description",
          content: `Map Observer's annual State of Local Search report — a free, citable benchmark of Google Business Profile, Map Pack, and AI search trends. Get notified when ${NEXT_REPORT_YEAR} drops.`,
        },
        { property: "og:title", content: `State of Local Search ${NEXT_REPORT_YEAR}` },
        { property: "og:description", content: "Annual benchmark of GBP, Map Pack, and AI search trends." },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        // Announcement page only — the substantive edition lives at
        // /state-of-local-search/<year>. Keep this out of the index until the
        // report itself exists here.
        { name: "robots", content: "noindex, follow" },
      ],
      links: [{ rel: "canonical", href: url }],

      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Report",
            name: `State of Local Search ${NEXT_REPORT_YEAR}`,
            description: "Annual practitioner benchmark of Google Business Profile, the Map Pack, and AI search trends.",
            url,
            author: { "@type": "Organization", name: SITE.name, url: SITE.url },
            publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
            datePublished: `${NEXT_REPORT_YEAR}-01-15`,
            inLanguage: "en",
          }),
        },
      ],
    };
  },
  component: ReportPage,
});

function ReportPage() {
  const { nextReportYear: NEXT_REPORT_YEAR, currentYear: THIS_YEAR } = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li>State of Local Search</li>
          </ol>
        </nav>
        <header className="mt-6">
          <p className="eyebrow">Annual benchmark</p>
          <h1 className="headline-display mt-3 text-5xl">State of Local Search {NEXT_REPORT_YEAR}</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            The free, citable benchmark of Google Business Profile, the Map Pack, and AI search —
            built from twelve months of the Map Observer Local Search Index and a survey of
            practising local SEOs.
          </p>
        </header>

        <div className="mt-8 border border-accent bg-paper-soft p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow">Live edition</p>
            <p className="mt-1 text-sm">A continuously-updated snapshot of {THIS_YEAR} is published as it happens.</p>
          </div>
          <Link to="/state-of-local-search/$year" params={{ year: String(THIS_YEAR) }} className="text-sm font-semibold text-accent underline">Read the {THIS_YEAR} edition →</Link>
        </div>


        <section className="mt-12 border-l-4 border-accent bg-paper-soft p-6">
          <p className="eyebrow">What's inside</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li>→ 12-month Map Pack volatility timeline</li>
            <li>→ Feature adoption: Products, Services, Posts, attributes, AI summaries</li>
            <li>→ AI Overview citation rate for local-intent queries</li>
            <li>→ Spam &amp; suspension trends by category</li>
            <li>→ Practitioner survey: what agencies are actually doing differently</li>
          </ul>
        </section>

        <section className="mt-12">
          <div className="border-b border-foreground pb-3 mb-6">
            <h2 className="headline-display text-2xl">Be first to read it</h2>
          </div>
          <p className="text-foreground/85">
            The {NEXT_REPORT_YEAR} edition publishes in January. Subscribers get it the week before
            it goes public. Free, no gating, citable on release.
          </p>
        </section>

        <NewsletterCTA />

        <div className="mt-12 flex items-center gap-3 border-t border-border pt-6">
          <CiteThis
            title={`State of Local Search ${NEXT_REPORT_YEAR}`}
            url={`${SITE.url}/state-of-local-search`}
            publishedDate={`${NEXT_REPORT_YEAR}-01-15`}
          />
          <span className="text-xs text-muted-foreground">
            Pre-cite this page; the URL will hold the published report on release.
          </span>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
